import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../utils/apiFetch.js';
import { downloadReceipt } from '../components/BookingReceipt.jsx';
import BookingChat from '../components/BookingChat.jsx';
import { formatDateOnly, parseDateOnly } from '../utils/dateOnly.js';
import ProgressStepper from '../components/ProgressStepper.jsx';
import MovdMoveProfile from '../design-system/MovdMoveProfile.jsx';
import MovdNotice from '../design-system/MovdNotice.jsx';
import MovdButton from '../design-system/MovdButton.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const money = value => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(Number(value || 0));

const statusCopy = {
  pending: 'Awaiting mover acceptance',
  confirmed: 'Mover accepted',
  in_progress: 'Move in progress',
  awaiting_customer_confirmation: 'Confirm completion',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
  payment_failed: 'Payment failed',
};

const acceptanceCopy = {
  interprovincial_route: 'Because this move crosses provincial borders, your mover is reviewing the route, crew, and equipment before confirming it.',
  capacity_verification: 'Your mover is reviewing the shipment details and vehicle capacity before confirming dispatch.',
  specialty_handling: 'Your mover is reviewing the specialty handling requirements and confirming the right equipment.',
};

const statusTone = status => (status === 'completed' ? movdTokens.color.state.success : status === 'cancelled' ? movdTokens.color.state.error : movdTokens.color.action.primary);

function Timeline({ status }) {
  const steps = [
    ['confirmed', 'Booking confirmed'],
    ['in_progress', 'Mover accepted'],
    ['awaiting_customer_confirmation', 'Moving day'],
    ['completed', 'Confirm completion'],
    ['review', 'Leave a review'],
  ];
  const order = ['pending', 'confirmed', 'in_progress', 'awaiting_customer_confirmation', 'completed'];
  const progress = order.indexOf(status);
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {steps.map(([key, label], index) => {
        const done = key === 'review' ? status === 'completed' : index <= Math.max(progress, 0);
        return (
          <div key={key} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span aria-hidden="true" style={{ color: done ? movdTokens.color.state.success : '#CBD5E1', fontWeight: 900 }}>{done ? '\u2713' : '\u25CB'}</span>
            <span style={{ fontSize: 14, fontWeight: done ? 700 : 400, color: done ? movdTokens.color.content.primary : movdTokens.color.content.secondary }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function CustomerBookingDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    apiFetch(`/api/customer/bookings/${bookingId}`, { credentials: 'include' })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Unable to load this booking.');
        setBooking(data);
      })
      .catch(loadError => setError(loadError.message))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const addToCalendar = () => {
    if (!booking?.moveDate) return;
    const date = parseDateOnly(booking.moveDate);
    const pad = value => String(value).padStart(2, '0');
    const ymd = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
    const title = encodeURIComponent(`Moving day - ${booking.mover?.name || 'MOVD'}`);
    const details = encodeURIComponent(`Booking reference: ${booking.jobNumber || booking._id}`);
    window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}T080000/${ymd}T180000&details=${details}&location=${encodeURIComponent(booking.origin || '')}`, '_blank', 'noopener');
  };

  const download = async () => {
    setDownloading(true);
    try {
      await downloadReceipt({
        bookingRef: booking.jobNumber || booking._id,
        selectedMover: { company: booking.mover?.name, rating: booking.mover?.rating, reviews: booking.mover?.reviewCount, services: booking.mover?.services, price: booking.price },
        serviceDetails: { origin: booking.origin, destination: booking.destination, moveDate: booking.moveDate, timeWindow: booking.timeWindow },
        bookingDetails: { contactInfo: { email: booking.contactEmail, phone: booking.contactPhone } },
        paymentInfo: { total: booking.payment?.totalAmount || booking.price, method: booking.payment?.method },
      });
    } finally { setDownloading(false); }
  };

  if (loading) return <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}><div aria-hidden="true" style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid rgba(0,0,0,.1)', borderTopColor: movdTokens.color.action.primary, animation: 'movd-spin 0.8s linear infinite' }} /><style>{'@keyframes movd-spin { to { transform: rotate(360deg); } }'}</style></div>;
  if (error || !booking) return <div style={{ maxWidth: 760, margin: '0 auto', padding: 24 }}><MovdNotice tone="error">{error || 'Booking not found.'}</MovdNotice></div>;

  const payment = booking.payment || {};
  const mover = booking.mover || {};
  const canConfirm = booking.status === 'awaiting_customer_confirmation' && payment._id;
  const move = {
    origin: booking.origin,
    destination: booking.destination,
    moveDate: booking.moveDate,
    inventory: booking.inventoryDetails || [],
  };

  return (
    <main style={{ maxWidth: 1040, margin: '0 auto', padding: 'clamp(16px, 3vw, 32px)', background: '#F1F5F9', minHeight: '100vh' }}>
      <div style={{ marginBottom: 16 }}><ProgressStepper currentStep={4} /></div>
      <div style={{ padding: 'clamp(16px, 3vw, 24px)', marginBottom: 20, background: movdTokens.color.ink, borderTop: `3px solid ${movdTokens.color.teal}` }}>
        <MovdMoveProfile move={move} dark selectedMover={{ company: mover.name, price: payment.totalAmount || booking.price }} />
      </div>
      <button type="button" onClick={() => navigate('/customer-portal')} style={{ background: 'transparent', border: 'none', color: movdTokens.color.content.secondary, fontWeight: 700, cursor: 'pointer', marginBottom: 16, padding: 0 }}>‹ Customer portal</button>

      <div style={{ padding: 'clamp(20px, 4vw, 32px)', border: '1px solid #D9E2EC', borderRadius: 10, marginBottom: 20, background: '#fff' }}>
        <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontWeight: 800, fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase' }}>Booking #{booking.jobNumber || booking._id}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 850 }}>{statusCopy[booking.status] || booking.status}</h1>
          <span style={{ background: statusTone(booking.status), color: '#fff', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 800 }}>{statusCopy[booking.status] || booking.status}</span>
        </div>
      </div>

      {booking.moverAcceptanceRequired && booking.status === 'pending' && (
        <MovdNotice tone="info" style={{ marginBottom: 20 }}>Your payment is secure in escrow. {acceptanceCopy[booking.moverAcceptanceReason] || 'Your mover is reviewing the move details before confirming it.'}</MovdNotice>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 20 }}>
          <div style={{ padding: 'clamp(20px, 3vw, 24px)', border: '1px solid #D9E2EC', borderRadius: 10, background: '#fff' }}>
            <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Your move</p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: movdTokens.color.brand.ink, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, overflow: 'hidden', flexShrink: 0 }}>
                {mover.logo || mover.profileImage ? <img src={mover.logo || mover.profileImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (mover.name?.charAt(0) || 'M')}
              </div>
              <div><p style={{ margin: 0, fontWeight: 850 }}>{mover.name || 'Moving company'}</p><p style={{ margin: 0, fontSize: 12, color: movdTokens.color.content.secondary }}>{mover.rating ? `★ ${mover.rating}` : 'MOVD mover'}</p></div>
            </div>
            <div style={{ height: 1, background: '#E2E8F0', margin: '20px 0' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div><p style={{ margin: 0, fontSize: 11, color: movdTokens.color.content.secondary, fontWeight: 700 }}>MOVE DATE</p><p style={{ margin: 0, fontWeight: 700 }}>{formatDateOnly(booking.moveDate, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p></div>
              <div><p style={{ margin: 0, fontSize: 11, color: movdTokens.color.content.secondary, fontWeight: 700 }}>ARRIVAL WINDOW</p><p style={{ margin: 0, fontWeight: 700 }}>{booking.timeWindow || 'To be confirmed'}</p></div>
            </div>
            <div style={{ height: 1, background: '#E2E8F0', margin: '20px 0' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 24px minmax(0,1fr)', gap: 12, alignItems: 'center' }}>
              <div><p style={{ margin: 0, fontSize: 11, color: movdTokens.color.content.secondary, fontWeight: 700 }}>PICKUP</p><p style={{ margin: 0, fontWeight: 700 }}>{booking.origin}</p></div>
              <span aria-hidden="true" style={{ color: movdTokens.color.action.primary, textAlign: 'center' }}>→</span>
              <div><p style={{ margin: 0, fontSize: 11, color: movdTokens.color.content.secondary, fontWeight: 700 }}>DROP-OFF</p><p style={{ margin: 0, fontWeight: 700 }}>{booking.destination}</p></div>
            </div>
            {(booking.inventoryDetails?.length || booking.additionalServices?.length) > 0 && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #E2E8F0' }}>
                <p style={{ margin: 0, fontSize: 11, color: movdTokens.color.content.secondary, fontWeight: 700 }}>MOVE DETAILS</p>
                <p style={{ margin: '4px 0 0', fontSize: 14 }}>{booking.inventoryDetails?.reduce((sum, item) => sum + Number(item.quantity || item.qty || 1), 0) || 0} items{booking.additionalServices?.length ? ` · ${booking.additionalServices.length} added services` : ''}</p>
              </div>
            )}
          </div>

          {canConfirm && (
            <MovdNotice tone="info">
              Your mover marked this move complete. Review it before escrow is released.
              <button type="button" onClick={() => navigate(`/move-complete/${payment._id}`)} style={{ display: 'block', marginTop: 8, background: 'transparent', border: 'none', color: 'inherit', fontWeight: 800, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>Review move</button>
            </MovdNotice>
          )}

          <div style={{ padding: 'clamp(20px, 3vw, 24px)', border: '1px solid #D9E2EC', borderRadius: 10, background: '#fff' }}>
            <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Booking conversation</p>
            <div style={{ marginTop: 12 }}><BookingChat bookingId={booking._id} /></div>
          </div>
        </div>

        <div style={{ display: 'grid', gap: 20 }}>
          <div style={{ padding: 24, background: movdTokens.color.brand.ink, color: '#fff', borderRadius: 10 }}>
            <p style={{ margin: 0, color: '#93C5FD', fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Payment</p>
            <p style={{ margin: '8px 0 0', color: 'rgba(255,255,255,.65)', fontSize: 12 }}>TOTAL PAID</p>
            <p style={{ margin: 0, fontSize: 32, fontWeight: 900 }}>{money(payment.totalAmount || booking.price)}</p>
            <p style={{ margin: '8px 0 0', color: 'rgba(255,255,255,.75)', fontSize: 14 }}>Payment received and held securely in escrow until completion.</p>
            <p style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 16, color: '#BFDBFE', fontSize: 12 }}><span aria-hidden="true">🔒</span> {payment.paymentStatus || 'Payment status pending'}</p>
          </div>
          <div style={{ padding: 24, border: '1px solid #D9E2EC', borderRadius: 10, background: '#fff' }}>
            <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Booking status</p>
            <div style={{ marginTop: 16 }}><Timeline status={booking.status} /></div>
          </div>
          {booking.activity?.length > 0 && (
            <div style={{ padding: 24, border: '1px solid #D9E2EC', borderRadius: 10, background: '#fff' }}>
              <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Activity</p>
              <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                {[...booking.activity].reverse().map((event, index) => (
                  <div key={`${event.createdAt}-${index}`}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{event.message}</p>
                    <p style={{ margin: 0, fontSize: 12, color: movdTokens.color.content.secondary }}>{new Date(event.createdAt).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'grid', gap: 10 }}>
            <MovdButton onClick={download} loading={downloading}>{downloading ? 'Preparing receipt' : 'Download receipt'}</MovdButton>
            <MovdButton variant="secondary" onClick={addToCalendar}>Add to calendar</MovdButton>
          </div>
        </div>
      </div>
    </main>
  );
}
